import axios from 'axios';
import { ANALYSIS_BANK, PORTFOLIO_ANALYSIS } from './analysisBank';

const CEREBRO_API = axios.create({
  baseURL: import.meta.env.VITE_INVESTMENTS_API_URL || 'http://localhost:5001',
  headers: { 'Content-Type': 'application/json' },
  timeout: 3000, // Quick timeout to fall back to offline mode
});

CEREBRO_API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ==================== OFFLINE FALLBACK ====================

let backendAvailable = null; // null = not checked, true/false = result
let backendCheckPromise = null;

async function checkBackend() {
  if (backendAvailable !== null) return backendAvailable;
  if (backendCheckPromise) return backendCheckPromise;

  backendCheckPromise = (async () => {
    try {
      await CEREBRO_API.get('/health', { timeout: 2000 });
      backendAvailable = true;
    } catch {
      backendAvailable = false;
      console.info('[Cerebro] Backend unavailable, using offline analysis bank');
    }
    return backendAvailable;
  })();

  return backendCheckPromise;
}

function getOfflineStockAnalysis(symbol, tier = 'basic') {
  const data = ANALYSIS_BANK[symbol.toUpperCase()];
  if (!data || !data[tier]) {
    return {
      symbol: symbol.toUpperCase(),
      tier,
      analysis: `Analisis para ${symbol.toUpperCase()} aun no disponible. El Cerebro lo agregara pronto.`,
      qualitative: null,
      fundamentals: null,
      technicals: null,
      source: 'unavailable',
      date: new Date().toISOString().split('T')[0],
    };
  }
  return {
    symbol: symbol.toUpperCase(),
    tier,
    analysis: data[tier],
    qualitative: tier === 'advanced' || tier === 'premium' ? data.qualitative : null,
    fundamentals: data.fundamentals || null,
    technicals: null,
    scenarios: ['advanced', 'premium'].includes(tier) ? (data.scenarios || null) : null,
    source: 'offline',
    date: new Date().toISOString().split('T')[0],
  };
}

function getOfflinePortfolioAnalysis(portfolioType, tier = 'basic') {
  const data = PORTFOLIO_ANALYSIS[portfolioType];
  if (!data || !data[tier]) {
    return {
      portfolioType,
      tier,
      analysis: 'Analisis del portafolio en preparacion.',
      source: 'unavailable',
    };
  }
  return {
    portfolioType,
    tier,
    analysis: data[tier],
    source: 'offline',
  };
}

// ==================== ANALYSIS ====================

export async function getStockAnalysis(symbol, tier = null) {
  const isUp = await checkBackend();
  const effectiveTier = tier || getEffectiveTier();

  if (!isUp) {
    return getOfflineStockAnalysis(symbol, effectiveTier);
  }

  try {
    const params = tier ? { tier } : {};
    const { data } = await CEREBRO_API.get(`/api/analysis/stock/${symbol}`, { params });
    return data.data;
  } catch {
    return getOfflineStockAnalysis(symbol, effectiveTier);
  }
}

/**
 * Returns analysis for ALL tiers in one call (for the tier-tabs UI).
 * Falls back to assembling all tiers from the offline bank when backend is down.
 */
export async function getStockAnalysisAllTiers(symbol) {
  const isUp = await checkBackend();

  if (isUp) {
    try {
      const { data } = await CEREBRO_API.get(`/api/analysis/stock/${symbol}`, {
        params: { tier: 'all' },
      });
      return data.data;
    } catch {
      // fall through to offline
    }
  }

  const upper = symbol.toUpperCase();
  const bank = ANALYSIS_BANK[upper];
  const tiers = { basic: null, intermediate: null, advanced: null, premium: null };
  if (bank) {
    for (const t of Object.keys(tiers)) tiers[t] = bank[t] || null;
  }
  return {
    symbol: upper,
    date: new Date().toISOString().split('T')[0],
    source: bank ? 'offline' : 'pending',
    tiers,
    qualitative: bank?.qualitative || null,
    fundamentals: bank?.fundamentals || null,
    technicals: null,
    userTier: getEffectiveTier(),
  };
}

export async function getPortfolioAnalysis(tier = null) {
  const isUp = await checkBackend();

  if (!isUp) {
    // Get portfolio from local profile
    const profileStr = localStorage.getItem('fp_investor_profile');
    const profile = profileStr ? JSON.parse(profileStr) : null;
    const portfolioType = profile?.portfolio || 'explorer';
    const effectiveTier = tier || getEffectiveTier();
    const result = getOfflinePortfolioAnalysis(portfolioType, effectiveTier);
    return {
      ...result,
      portfolio: portfolioType,
      thesis: profile?.thesis || '',
      userTier: effectiveTier,
    };
  }

  try {
    const params = tier ? { tier } : {};
    const { data } = await CEREBRO_API.get('/api/analysis/portfolio', { params });
    return data.data;
  } catch {
    const profileStr = localStorage.getItem('fp_investor_profile');
    const profile = profileStr ? JSON.parse(profileStr) : null;
    const portfolioType = profile?.portfolio || 'explorer';
    const effectiveTier = tier || getEffectiveTier();
    return {
      ...getOfflinePortfolioAnalysis(portfolioType, effectiveTier),
      portfolio: portfolioType,
      thesis: profile?.thesis || '',
      userTier: effectiveTier,
    };
  }
}

export async function getAvailableSymbols() {
  // Fuente de verdad: el backend une banco + cache (incluye acciones nuevas como CAI).
  // Cae al banco local solo si el backend no responde.
  const isUp = await checkBackend();
  if (isUp) {
    try {
      const { data } = await CEREBRO_API.get('/api/analysis/symbols');
      if (Array.isArray(data?.data) && data.data.length) return data.data;
    } catch {
      // fall through to offline
    }
  }
  return Object.keys(ANALYSIS_BANK);
}

export async function getSnapshot(symbol) {
  const isUp = await checkBackend();
  if (!isUp) return null;
  try {
    const { data } = await CEREBRO_API.get(`/api/analysis/snapshot/${symbol}`);
    return data.data;
  } catch {
    return null;
  }
}

export async function getTechnicals(symbol) {
  const isUp = await checkBackend();
  if (!isUp) return null;
  try {
    const { data } = await CEREBRO_API.get(`/api/analysis/technicals/${symbol}`);
    return data.data;
  } catch {
    return null;
  }
}

// ==================== INVESTOR ====================

export async function completeOnboarding(answers) {
  const isUp = await checkBackend();

  if (!isUp) {
    // Calculate locally and save to localStorage
    const portfolio = calculateLocalPortfolio(answers);
    const thesis = generateLocalThesis(portfolio);
    const profile = {
      answers,
      portfolio,
      thesis,
      tier: 'basic',
      level: 1,
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem('fp_investor_profile', JSON.stringify(profile));
    return profile;
  }

  try {
    const { data } = await CEREBRO_API.post('/api/investor/onboarding', { answers });
    localStorage.setItem('fp_investor_profile', JSON.stringify({ ...data.data, answers }));
    return data.data;
  } catch {
    const portfolio = calculateLocalPortfolio(answers);
    const thesis = generateLocalThesis(portfolio);
    const profile = { answers, portfolio, thesis, tier: 'basic', level: 1, createdAt: new Date().toISOString() };
    localStorage.setItem('fp_investor_profile', JSON.stringify(profile));
    return profile;
  }
}

/**
 * Creates an Alpaca brokerage account for the authenticated user.
 * Called right after onboarding completes (auto-provision).
 * Returns { alpacaAccountId, status, funded } on success, null on failure.
 */
export async function createAlpacaAccount({ firstName, lastName, phoneNumber, initialFundUsd = 5000 }) {
  const isUp = await checkBackend();
  if (!isUp) return null;
  try {
    const { data } = await CEREBRO_API.post('/api/investor/create-alpaca-account', {
      firstName,
      lastName,
      phoneNumber,
      initialFundUsd,
    });
    return data.data;
  } catch (err) {
    const msg = err?.response?.data?.message || err.message;
    console.error('[Cerebro] createAlpacaAccount failed:', msg);
    return null;
  }
}

export async function getInvestorProfile() {
  const isUp = await checkBackend();

  if (!isUp) {
    const profileStr = localStorage.getItem('fp_investor_profile');
    if (profileStr) return JSON.parse(profileStr);
    throw new Error('No profile found');
  }

  try {
    const { data } = await CEREBRO_API.get('/api/investor/profile');
    return data.data;
  } catch {
    const profileStr = localStorage.getItem('fp_investor_profile');
    if (profileStr) return JSON.parse(profileStr);
    throw new Error('No profile found');
  }
}

export async function getLevel() {
  const isUp = await checkBackend();

  if (!isUp) {
    const stored = localStorage.getItem('fp_user_level');
    if (stored) return JSON.parse(stored);
    return {
      level: 1,
      achievements: [],
      lessonsCompleted: [],
      totalInvested: 0,
      stocksViewed: [],
    };
  }

  try {
    const { data } = await CEREBRO_API.get('/api/investor/level');
    return data.data;
  } catch {
    return { level: 1, achievements: [], lessonsCompleted: [], totalInvested: 0, stocksViewed: [] };
  }
}

export async function completeLesson(lessonId) {
  const isUp = await checkBackend();
  if (!isUp) {
    const stored = localStorage.getItem('fp_user_level');
    const level = stored ? JSON.parse(stored) : { lessonsCompleted: [] };
    if (!level.lessonsCompleted.includes(lessonId)) {
      level.lessonsCompleted.push(lessonId);
    }
    localStorage.setItem('fp_user_level', JSON.stringify(level));
    return level;
  }
  try {
    const { data } = await CEREBRO_API.post(`/api/investor/lesson/${lessonId}`);
    return data.data;
  } catch {
    return { lessonsCompleted: [lessonId], newAchievements: [] };
  }
}

export async function updateTier(equity) {
  const isUp = await checkBackend();
  if (!isUp) return { tier: 'basic', equity };
  try {
    const { data } = await CEREBRO_API.post('/api/investor/update-tier', { equity });
    return data.data;
  } catch {
    return { tier: 'basic', equity };
  }
}

export async function checkLevelUp() {
  const isUp = await checkBackend();
  if (!isUp) return { leveledUp: false };
  try {
    const { data } = await CEREBRO_API.post('/api/investor/check-levelup');
    return data.data;
  } catch {
    return { leveledUp: false };
  }
}

// ==================== HEALTH ====================

export async function checkHealth() {
  try {
    const { data } = await CEREBRO_API.get('/health');
    return data;
  } catch {
    return { status: 'offline', mode: 'frontend-only' };
  }
}

// ==================== DEMO MODE ====================

const DEMO_KEY = 'fp_demo_tier';

export function getDemoTier() {
  return localStorage.getItem(DEMO_KEY) || null;
}

export function setDemoTier(tier) {
  if (tier) localStorage.setItem(DEMO_KEY, tier);
  else localStorage.removeItem(DEMO_KEY);
}

export function toggleDemoTier() {
  // Cycle: null (real) → basic → intermediate → advanced → premium → null
  const current = getDemoTier();
  const cycle = [null, 'basic', 'intermediate', 'advanced', 'premium'];
  const currentIndex = cycle.indexOf(current);
  const nextIndex = (currentIndex + 1) % cycle.length;
  const nextTier = cycle[nextIndex];
  setDemoTier(nextTier);
  return nextTier;
}

function getEffectiveTier() {
  const demo = getDemoTier();
  if (demo) return demo;
  const profileStr = localStorage.getItem('fp_investor_profile');
  if (profileStr) {
    try {
      return JSON.parse(profileStr).tier || 'basic';
    } catch {}
  }
  return 'basic';
}

// ==================== LOCAL HELPERS ====================

function calculateLocalPortfolio(answers) {
  if (answers.reactionToDrop === 'sell' || (answers.timeHorizon === 'short' && answers.excitement === 'safe_growth')) {
    return 'guardian';
  }
  if (answers.excitement === 'passive_income') {
    return 'income_builder';
  }
  if (answers.reactionToDrop === 'buy_more' && (answers.timeHorizon === 'long' || answers.timeHorizon === 'unlimited') && answers.excitement === 'world_changers') {
    return 'visionary';
  }
  return 'explorer';
}

function generateLocalThesis(portfolio) {
  const theses = {
    guardian: 'Tu portafolio Guardian esta disenado para proteger tu capital mientras crece de forma estable. Priorizamos ETFs diversificados, bonos y activos defensivos. Tu perfil indica que prefieres dormir tranquilo a perseguir retornos agresivos — y eso es inteligente.',
    explorer: 'Tu portafolio Explorador combina crecimiento con proteccion. Tienes exposicion al mercado americano, mercados emergentes y activos defensivos. Es un balance para alguien que quiere crecer pero sin montarse en una montana rusa.',
    visionary: 'Tu portafolio Visionario apuesta por las empresas que estan cambiando el mundo. Sabes que la volatilidad es temporal y que las mejores empresas se ven "caras" en todo momento. Tu horizonte largo te da una ventaja que la mayoria no tiene: paciencia.',
    income_builder: 'Tu portafolio Constructor de Ingresos esta disenado para generar dividendos crecientes ano tras ano. En lugar de perseguir subidas de precio, estas construyendo una maquina de ingresos pasivos que trabaja para ti 24/7.',
  };
  return theses[portfolio] || theses.explorer;
}

export default CEREBRO_API;
